import {Component, computed, inject, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslocoPipe} from '@jsverse/transloco';
import {filter, firstValueFrom, take} from 'rxjs';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {DirectusService, DirectusTranslationPipe, HrTool} from '@frontend/directus';
import {Card} from 'primeng/card';
import {
  ParticipatingToolControllerService,
  TenderPhaseType,
  TenderStatus,
  ProjectDecisionControllerService,
  HrToolTotalFeesPerProviderResponse,
} from '@frontend/shared-api';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {ProjectStepBaseReactive} from '../../../project-base-step/project-base-step-reactive.component';
import {PreviousStepIncompleteNoticeComponent} from '../../../../../../shared/components/previous-step-incomplete-notice/previous-step-incomplete-notice.component';
import {ProjectChapterType, ProjectStepType, AppCurrencyPipe, CurrencyData} from '@frontend/shared';
import {
  OverallScoreResult,
  OverallScoreService,
} from '../../../../../../shared/services/overall-score.service';
import {ExecutiveSummaryComponent} from './components/executive-summary/executive-summary.component';
import {DetailedAnalysisComponent} from './components/detailed-analysis/detailed-analysis.component';
import {CostComparisonComponent} from './components/cost-comparison/cost-comparison.component';
import {ModuleComparisonComponent} from './components/module-comparison/module-comparison.component';
import {
  OVERALL_RESULT_VIEW,
  OverallResultView,
  ProviderOverallResult,
  SummaryTile,
} from './types/overall-results.types';
import {TabStatusEnum} from '../../../../../../shared/tabs/tab-status.model';
import {TabStatusDirective} from '../../../../../../shared/tabs/tab-status.directive';
import {LoadingComponent} from '../../../../../../shared/components/loading/loading.component';
import {TabsUnsavedRevealDirective} from '../../../../../../shared/tabs/tabs-unsaved-reveal.directive';

@Component({
  selector: 'app-step-9-1-overall-results-overview',
  imports: [
    CommonModule,
    TranslocoPipe,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    Card,
    PreviousStepIncompleteNoticeComponent,
    ExecutiveSummaryComponent,
    DetailedAnalysisComponent,
    CostComparisonComponent,
    ModuleComparisonComponent,
    AppCurrencyPipe,
    LoadingComponent,
    TabStatusDirective,
    TabsUnsavedRevealDirective,
  ],
  providers: [DirectusTranslationPipe],
  templateUrl: './step-9-1-overall-results-overview.component.html',
  styleUrl: './step-9-1-overall-results-overview.component.scss',
})
export class Step91OverallResultsOverviewComponent extends ProjectStepBaseReactive {
  private readonly OverallScoreService = inject(OverallScoreService);
  private readonly participatingToolService = inject(ParticipatingToolControllerService);
  private readonly directusService = inject(DirectusService);
  private readonly directusTranslationPipe = inject(DirectusTranslationPipe);
  private readonly projectDecisionController = inject(ProjectDecisionControllerService);

  protected readonly OVERALL_RESULT_VIEW = OVERALL_RESULT_VIEW;
  protected readonly completedTabStatus = TabStatusEnum.COMPLETED;

  protected readonly isStepPrerequisiteCompleted = computed(() =>
    this.stepState.areStepsCompleted([
      {chapterId: ProjectChapterType.RFQ, stepId: ProjectStepType.RFQ_AGGREGATED_RESULTS},
    ]),
  );

  protected readonly selectedView = signal<OverallResultView>(
    OVERALL_RESULT_VIEW.EXECUTIVE_SUMMARY,
  );
  protected readonly phaseScoreResult = signal<OverallScoreResult | undefined>(undefined);
  protected readonly hrTools = signal<HrTool[]>([]);
  protected readonly isLoadingData = signal(false);
  protected readonly completedRfqToolIds = signal<string[]>([]);
  protected readonly rfqTotalsByToolId = signal<
    Map<string, {total: number; currencyData: CurrencyData}>
  >(new Map());

  protected readonly providerResults = computed((): ProviderOverallResult[] => {
    const result = this.phaseScoreResult();
    const tools = this.hrTools();
    if (!result || tools.length === 0) return [];

    const rfqTotals = this.rfqTotalsByToolId();

    const lang = this.translocoService.getActiveLang();

    return Array.from(result.toolScores.values())
      .sort((a, b) => b.overallPercentage - a.overallPercentage)
      .map((scores, index) => {
        const hrTool = tools.find((t) => t.id.toString() === scores.hrToolDirectusId);
        if (!hrTool) return undefined;

        const rfqTotal = rfqTotals.get(scores.hrToolDirectusId);

        const translatedName =
          this.directusTranslationPipe.transform(hrTool.translations, lang, hrTool.name) ??
          hrTool.name;

        return {
          hrTool,
          translatedName,
          scores,
          rank: index + 1,
          rfqTotalCostThreeYears: rfqTotal?.total,
          rfqCurrencyData: rfqTotal?.currencyData,
        } as ProviderOverallResult;
      })
      .filter((r): r is ProviderOverallResult => r !== undefined);
  });

  protected readonly summaryTiles = computed((): SummaryTile[] => {
    const results = this.providerResults();
    if (results.length === 0) return [];

    const winner = results.reduce((best, r) =>
      r.scores.overallPercentage > best.scores.overallPercentage ? r : best,
    );

    const providersWithCost = results.filter(
      (r) => (r.rfqTotalCostThreeYears ?? r.scores.totalCostThreeYears) > 0,
    );
    const bestValue = providersWithCost.reduce((best, r) => {
      const bestCost = best.rfqTotalCostThreeYears ?? best.scores.totalCostThreeYears;
      const currentCost = r.rfqTotalCostThreeYears ?? r.scores.totalCostThreeYears;
      if (bestCost <= 0) return r;
      if (currentCost <= 0) return best;
      return currentCost < bestCost ? r : best;
    }, providersWithCost[0] ?? winner);

    const bestFunctionalFit = results.reduce((best, r) =>
      r.scores.rfpPercentage > best.scores.rfpPercentage ? r : best,
    );

    const demoWinner = results.reduce((best, r) =>
      r.scores.rfdPercentage > best.scores.rfdPercentage ? r : best,
    );

    return [
      {
        labelKey: 'project.steps.overallResults.tiles.overallWinner',
        icon: 'ph-bold ph-trophy',
        toolName: winner.translatedName,
        value: `${Math.round(winner.scores.overallPercentage)}%`,
        variant: 'winner',
      },
      {
        labelKey: 'project.steps.overallResults.tiles.bestFunctionalFit',
        icon: 'pi pi-chart-bar',
        toolName: bestFunctionalFit.translatedName,
        value: `${Math.round(bestFunctionalFit.scores.rfpPercentage)}%`,
        variant: 'functional-fit',
      },
      {
        labelKey: 'project.steps.overallResults.tiles.demoWinner',
        icon: 'pi pi-video',
        toolName: demoWinner.translatedName,
        value: `${Math.round(demoWinner.scores.rfdPercentage)}%`,
        variant: 'demo',
      },
      {
        labelKey: 'project.steps.overallResults.tiles.bestValue',
        icon: 'pi pi-euro',
        toolName: bestValue.translatedName,
        value: (
          bestValue.rfqTotalCostThreeYears ?? bestValue.scores.totalCostThreeYears
        ).toString(),
        variant: 'value',
        currencyData: bestValue.rfqCurrencyData ?? bestValue.scores.currencyData,
      },
    ];
  });

  constructor() {
    super();
    toObservable(this.projectId)
      .pipe(
        filter((id): id is number => id !== undefined),
        take(1),
        takeUntilDestroyed(),
      )
      .subscribe((id) => void this.loadData(id));
  }

  private async loadData(id: number): Promise<void> {
    this.isLoadingData.set(true);
    try {
      const [completedToolIds, phaseScoreResult, rfqTotals] = await Promise.all([
        this.loadCompletedRfqToolIds(id),
        this.OverallScoreService.loadOverallScores(id),
        this.loadRfqTotals(id),
      ]);

      this.completedRfqToolIds.set(Array.from(completedToolIds));

      const filteredResult = this.filterToCompletedTools(phaseScoreResult, completedToolIds);
      this.phaseScoreResult.set(filteredResult);

      const hrTools = await this.loadHrToolsForScores(filteredResult);
      this.hrTools.set(hrTools);
      this.rfqTotalsByToolId.set(rfqTotals);
    } catch (error) {
      console.error('Error loading overall results data:', error);
    } finally {
      this.isLoadingData.set(false);
    }
  }

  private async loadCompletedRfqToolIds(projectId: number): Promise<Set<string>> {
    const response = await firstValueFrom(
      this.participatingToolService.getAllParticipatingTools({
        projectId,
        phase: TenderPhaseType.RFQ,
      }),
    ).catch(() => ({tools: [], currentPhase: null, maxAllowedParticipants: 0}));

    const completedIds = response.tools
      .filter(
        (tool) =>
          tool.rfqStatus === TenderStatus.FINISHED || tool.rfqStatus === TenderStatus.ACCEPTED,
      )
      .map((tool) => tool.hrToolDirectusId);

    return new Set(completedIds);
  }

  private filterToCompletedTools(
    result: OverallScoreResult,
    completedToolIds: Set<string>,
  ): OverallScoreResult {
    const filteredScores = new Map(
      Array.from(result.toolScores.entries()).filter(([toolId]) => completedToolIds.has(toolId)),
    );
    return {...result, toolScores: filteredScores};
  }

  private async loadHrToolsForScores(result: OverallScoreResult): Promise<HrTool[]> {
    const toolIds = Array.from(result.toolScores.keys());
    if (toolIds.length === 0) return [];
    return firstValueFrom(this.directusService.loadHrToolsByIds(toolIds));
  }

  protected onViewChange(viewId: string | number): void {
    if (typeof viewId === 'string') {
      this.selectedView.set(viewId as OverallResultView);
    }
  }
  private async loadRfqTotals(
    projectId: number,
  ): Promise<Map<string, {total: number; currencyData: CurrencyData}>> {
    const response = await firstValueFrom(
      this.projectDecisionController.getTotalFees({projectId}),
    ).catch((): HrToolTotalFeesPerProviderResponse[] => []);

    const totals = new Map<string, {total: number; currencyData: CurrencyData}>();

    response.forEach((item) => {
      const currencyData: CurrencyData = {
        locale: item.locale,
        currencyCode: item.currencyCode,
      };

      totals.set(item.providerTenantMappingResponse.hrToolDirectusId, {
        total: item.hrToolTotalFees.totalAllYearsFees,
        currencyData,
      });
    });

    return totals;
  }
}
